import { CartLinesUpdateEvent } from '@shopify/events';

/** Mix and match carousel in the cart drawer: pick a colourway swatch, then add it to the cart. */
class RumeMixMatch extends HTMLElement {
  /** @type {{x: number, y: number} | null} */
  #down = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerdown', (event) => {
      this.#down = { x: event.clientX, y: event.clientY };
    });
    this.addEventListener('pointerup', (event) => {
      const down = this.#down;
      this.#down = null;
      if (!down || event.pointerType === 'mouse') return;
      if (Math.abs(event.clientX - down.x) > 8 || Math.abs(event.clientY - down.y) > 8) return;
      const swatch = /** @type {HTMLElement} */ (event.target).closest('[data-swatch]');
      if (swatch instanceof HTMLButtonElement) this.#select(swatch);
    });
    this.querySelectorAll('[data-swatches]').forEach((list) => {
      const thumb = list.parentElement.querySelector('[data-thumb]');
      const sync = () => {
        const { scrollHeight, clientHeight, scrollTop } = list;
        if (!scrollHeight) return;
        const bar = thumb.parentElement;
        const overflow = scrollHeight > clientHeight + 1;
        if (bar.hidden === overflow) bar.hidden = !overflow;
        const height = `${Math.max((clientHeight / scrollHeight) * 100, 12)}%`;
        const top = `${(scrollTop / scrollHeight) * 100}%`;
        if (thumb.style.height !== height) thumb.style.height = height;
        if (thumb.style.top !== top) thumb.style.top = top;
      };
      list.addEventListener('scroll', sync, { passive: true });
      requestAnimationFrame(sync);
      new ResizeObserver(sync).observe(list);
      new MutationObserver(sync).observe(list.parentElement, { attributes: true, subtree: true, attributeFilter: ['hidden', 'style'] });
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const track = this.querySelector('[data-track]');

    if (target.closest('[data-prev]')) return void track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });
    if (target.closest('[data-next]')) return void track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });

    const swatch = target.closest('[data-swatch]');
    if (swatch instanceof HTMLButtonElement) return void this.#select(swatch);

    const add = target.closest('[data-add]');
    if (add instanceof HTMLButtonElement) this.#add(add);
  };

  /** @param {HTMLButtonElement} swatch */
  #select(swatch) {
    if (swatch.disabled) return;
    const card = swatch.closest('[data-card]');
    card.querySelectorAll('[data-swatch]').forEach((s) => s.classList.toggle('is-selected', s === swatch));
    card.querySelector('[data-img]').src = swatch.dataset.image;
    card.querySelector('[data-colour]').textContent = swatch.dataset.colour;
    card.querySelector('[data-price]').textContent = swatch.dataset.price;
    card.querySelector('[data-add]').dataset.variant = swatch.dataset.variant;
  }

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    if (button.disabled) return;
    const id = Number(button.dataset.variant);
    if (!id) return;
    button.disabled = true;
    const original = button.textContent;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sectionIds.join(',') }),
      }).then((r) => r.json());
      const cart = await fetch(`${Theme.routes.cart_url}.json`).then((r) => r.json());
      const failed = Boolean(response.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'rume-mix-match',
          itemCount: 1,
          sections: response.sections,
          didError: failed,
        },
      });
      button.textContent = failed ? 'Unavailable' : 'Added ✓';
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      button.textContent = 'Try again';
    }

    setTimeout(() => {
      button.textContent = original;
      button.disabled = false;
    }, 1200);
  }
}

if (!customElements.get('rume-mix-match')) customElements.define('rume-mix-match', RumeMixMatch);
